/*
 * The Jolly Clock-Out — backing email verification (M12C).
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY THIS PAGE EXISTS AT ALL
 * ──────────────────────────────────────────────────────────────────────────
 * `POST /apps/jolly-clockout/backings/verify` is the only endpoint that
 * increments an Office's backing count, and it is POST-only on purpose:
 * mailbox security scanners (Defender for Office 365, Proofpoint and similar)
 * follow links in email automatically, and a GET that confirmed a backing
 * would let a scanner confirm on the recipient's behalf — exactly the fraud
 * the double opt-in exists to prevent.
 *
 * So the emailed link opens THIS page, and nothing happens until a human
 * presses the button. There is deliberately no auto-submit, no auto-submit
 * after a delay, and no submit on any event a scanner can trigger.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * THE TOKEN
 * ──────────────────────────────────────────────────────────────────────────
 * It arrives in `?t=` and is read here, in the browser, only. It is never
 * rendered into the HTML by Liquid, never written to a DOM attribute, never
 * stored, never logged, never put in an outbound link and never included in
 * any message shown to the visitor. It is removed from the visible URL as soon
 * as it has been read, so it does not linger in history, in a screenshot or in
 * a Referer header, and it lives only in the closure below.
 *
 * Thrown errors are never inspected or logged either: a rejected `fetch` can
 * carry the request — and therefore the token — inside the error object.
 */
(function () {
  "use strict";

  /* ----------------------------------------------------------------------
   * Campaign credential store (M21).
   *
   * The SAME `jco:v1` object the Office Wall owns — same key, same shape, not
   * a second store. This page only ever MERGES into it: it reads what is
   * there, adds the office-scoped keys it has just earned, and writes the
   * whole object back. Dropping a namespace here would silently delete the
   * visitor's grants, entry edit credentials or remembered name, because the
   * Wall's own reader reconstructs the object from named keys.
   *
   * Nothing is read back out of here by this page, and no credential is ever
   * rendered, logged or put in a URL.
   * -------------------------------------------------------------------- */
  var STORE_KEY = "jco:v1";

  function mergeCampaignStore(slug, fields) {
    if (typeof slug !== "string" || !slug) return;
    if (!fields || typeof fields !== "object") return;

    try {
      var parsed = null;
      try {
        parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
      } catch (e) {
        parsed = null;
      }
      if (!parsed || typeof parsed !== "object") parsed = {};

      function bucket(name) {
        if (!parsed[name] || typeof parsed[name] !== "object") parsed[name] = {};
        return parsed[name];
      }

      /* M28 — THE PARTICIPANT GRANT.
       *
       * Written into the SAME `grants` namespace the Office Wall already reads,
       * in the same `{ token, expiresAt }` shape, and `lastOffice` is set
       * alongside it exactly as saveGrant() does. No parallel authentication is
       * introduced: on arrival the Wall's own resolveCredential() finds this
       * grant and loads in participant mode, which is what lets a coworker who
       * has never held a share link reach the Wall at all.
       *
       * A grant is READ access to one office and nothing more. It is written
       * here on its own account and never stands in for the contributor or the
       * share credential — those are separate fields below, and a response that
       * omits them leaves the visitor without those powers rather than
       * inheriting them from this one.
       *
       * `expiresAt` is null when the backend sends none; the Wall keeps a grant
       * with a null expiry and prunes only ones that have genuinely lapsed. */
      if (typeof fields.participantToken === "string" && fields.participantToken) {
        var expiresAt =
          typeof fields.participantTokenExpiresAt === "string" && fields.participantTokenExpiresAt
            ? fields.participantTokenExpiresAt
            : null;
        bucket("grants")[slug] = { token: fields.participantToken, expiresAt: expiresAt };
        parsed.lastOffice = slug;
      }

      if (typeof fields.contributorToken === "string" && fields.contributorToken) {
        /* M22 — `source` records WHICH route issued this credential. A
         * backing-sourced one does NOT own the office profile, and the backend
         * refuses it on every profile route whatever this file records.
         *
         * NEVER DOWNGRADES. A nominator who also confirms a backing — their
         * own office, from a second device, or an office they were nominated
         * to — must not lose profile ownership because the backing credential
         * arrived second. The token is replaced with the fresh one either way;
         * only the source is preserved. */
        var existing = bucket("contributors")[slug];
        var owner =
          existing && typeof existing === "object" && existing.source === "nomination";
        bucket("contributors")[slug] = {
          token: fields.contributorToken,
          source: owner ? "nomination" : "backing"
        };
      }
      if (typeof fields.accessToken === "string" && fields.accessToken) {
        bucket("share")[slug] = fields.accessToken;
      }
      /* M21 — THIS browser's own backing is now confirmed for THIS office.
       *
       * Written only here, on a successful `backings/verify` response, which
       * is the one moment anything in the campaign knows it. The Office Wall
       * reads it for the first status row; it is never sent to the backend and
       * is not authorization. Nomination verification deliberately does NOT
       * write it — a verified nominator has not backed, and conflating the two
       * is exactly what this records separately to avoid. */
      if (fields.backingVerified === true) {
        bucket("backed")[slug] = "verified";
      }

      window.localStorage.setItem(STORE_KEY, JSON.stringify(parsed));
    } catch (e) {
      /* Storage can be unavailable or full. The visitor simply has to reopen
       * the emailed link on the device where they want to contribute, which
       * is the documented fallback — never a thrown error on a success page. */
    }
  }

  var VERIFY_URL = "/apps/jolly-clockout/backings/verify";
  var TOKEN_PARAM = "t";

  /* The backend's own shape check is /^[A-Za-z0-9_-]{43}$/. It is deliberately
   * NOT duplicated here: only presence is checked, so a future change to the
   * token format cannot make this page refuse links the backend would accept.
   * A malformed token simply comes back as `reason: "invalid"`. */
  var WALL_PATH = "/pages/jolly-clock-out";

  /* EU-ONLY DIVERGENCE FROM THE US SOURCE.
   *
   * opposuits.eu serves path-prefixed locales (/fr, /es, /it). Resolving
   * WALL_PATH against the bare origin drops that prefix, so a visitor who
   * confirms under /fr is handed an English Office Wall link.
   *
   * The prefix is read from `window.Shopify.routes.root`, Shopify's own
   * global, which is "/" on a single-language storefront and "/fr/" under a
   * locale. It is not merchant-editable, so the security rationale above --
   * never let a settable value steer a URL carrying the share credential --
   * is unaffected. Anything unexpected falls back to "/", i.e. exactly the
   * US behaviour. */
  function localeRoot() {
    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      /* STRICT ALLOW-LIST. Must be the site root or a single Shopify Markets
       * locale segment: "/", "/fr/", "/pt-br/". The leading negative lookahead
       * is the important part — "//evil.test" and "//" both begin with "/" and
       * both change the HOST once resolved against an origin, which is exactly
       * how an open redirect is smuggled through a path check. Backslashes,
       * traversal, whitespace, encoded slashes and multi-segment paths all fail
       * the pattern and fall through to "/". */
      if (typeof root === "string" && /^\/(?!\/)(?:[a-z]{2}(?:-[a-z]{2})?)?\/?$/i.test(root)) {
        return root.charAt(root.length - 1) === "/" ? root : root + "/";
      }
    } catch (e) {}
    return "/";
  }

  function wallPath() {
    return localeRoot() + WALL_PATH.replace(/^\//, "");
  }

  /* Builds the Office Wall URL and PROVES it stayed on this origin.
   *
   * Defence in depth behind localeRoot(). Every caller goes through here, so if
   * a host-changing locale root ever slips past the pattern above, the result is
   * discarded and the plain unprefixed path is used instead. The share link this
   * page hands over carries the Office access token in its fragment, so an
   * off-origin URL would not merely redirect a visitor — it would hand that
   * credential to another host. The path is re-checked too, so a root that
   * resolved on-origin but somewhere unexpected is refused as well. */
  function wallUrlBase() {
    var fallback = new URL(WALL_PATH, window.location.origin);
    try {
      var candidate = new URL(wallPath(), window.location.origin);
      if (candidate.origin !== window.location.origin) return fallback;
      if (candidate.pathname.indexOf(WALL_PATH) === -1) return fallback;
      return candidate;
    } catch (e) {
      return fallback;
    }
  }

  /* The Office Wall link offered after a confirmed backing.
   *
   * Built ONLY from a read capability this browser already holds for that
   * office, in this order:
   *
   *   1. the raw read-only share token kept for that office, which produces
   *      the canonical link a coworker could also use;
   *   2. a participant grant, in which case `?office=<slug>` is enough — the
   *      Wall resolves a stored grant on its own and needs no fragment.
   *
   * Returns "" when neither exists. The backing-verification response carries
   * no access token, so there is nothing to synthesise one from, and a link
   * that lands on the private-access error would be worse than an honest note.
   *
   * The slug is an identifier, not a credential. A contributor credential and
   * an entry edit token are NEVER placed in a URL. */
  function officeWallUrl(slug) {
    if (typeof slug !== "string" || !slug) return "";

    var share = null;
    var hasGrant = false;
    try {
      var parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
      if (parsed && typeof parsed === "object") {
        if (parsed.share && typeof parsed.share === "object") {
          var token = parsed.share[slug];
          if (typeof token === "string" && token) share = token;
        }
        if (parsed.grants && typeof parsed.grants === "object") {
          var grant = parsed.grants[slug];
          hasGrant = !!(grant && typeof grant === "object" && grant.token);
        }
      }
    } catch (e) {
      return "";
    }

    if (!share && !hasGrant) return "";

    try {
      var url = wallUrlBase();
      url.searchParams.set("office", slug);

      /* Keep an unpublished-theme preview alive across the hop, so testing the
       * flow on a DEV theme does not bounce the tester onto the live one. Only
       * these two Shopify parameters are carried, never anything else from
       * this page's query string — `t` in particular must not travel. */
      try {
        var here = new URL(window.location.href);
        var preview = here.searchParams.get("preview_theme_id");
        var view = here.searchParams.get("view");
        if (preview) url.searchParams.set("preview_theme_id", preview);
        if (view) url.searchParams.set("view", "jolly-clockout");
      } catch (e) {
        /* A preview that cannot be carried is not a reason to fail. */
      }

      if (share) url.hash = "access=" + encodeURIComponent(share);
      return url.toString();
    } catch (e) {
      return "";
    }
  }

  /* H-1 — snippets/campaign-token-guard.liquid normally took the token out of
   * the address bar before content_for_header ran, and hands it over once.
   * `?t=` (today's links) and `#t=` (the future format) are both still read
   * here as the fallback. */
  function readToken() {
    try {
      var early =
        typeof window.__jcoTakeUrlCredentials === "function" ? window.__jcoTakeUrlCredentials() : null;
      if (early && typeof early.t === "string" && early.t) return early.t;
    } catch (e) {
      /* Fall through to the URL. */
    }
    try {
      var url = new URL(window.location.href);
      var fromQuery = url.searchParams.get(TOKEN_PARAM);
      if (fromQuery) return fromQuery;
      if (url.hash.length > 1) return new URLSearchParams(url.hash.slice(1)).get(TOKEN_PARAM) || "";
    } catch (e) {
      /* Unreadable URL: no token. */
    }
    return "";
  }

  /* Drop `?t=` / `#t=` from the address bar without reloading. Any other query
   * parameter is preserved, so this cannot break an unrelated link. */
  function stripTokenFromUrl() {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      var inHash = url.hash.length > 1 && new URLSearchParams(url.hash.slice(1)).has(TOKEN_PARAM);
      if (!url.searchParams.has(TOKEN_PARAM) && !inHash) return;
      url.searchParams.delete(TOKEN_PARAM);
      if (inHash) url.hash = "";
      var next = url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash;
      window.history.replaceState(window.history.state, "", next);
    } catch (e) {
      /* An address bar that cannot be rewritten is not a reason to fail. */
    }
  }

  function readStrings(root) {
    var node = root.querySelector("[data-jcv-strings]");
    if (!node) return {};
    try {
      var parsed = JSON.parse(node.textContent || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  /* Text only — never innerHTML. The company name comes from the backend, so it
   * is treated as untrusted content and inserted as a text node. */
  function setMessage(el, template, company) {
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    var parts = String(template || "").split("[company]");
    for (var i = 0; i < parts.length; i++) {
      el.appendChild(document.createTextNode(parts[i]));
      if (i < parts.length - 1) {
        var strong = document.createElement("strong");
        strong.appendChild(document.createTextNode(company || ""));
        el.appendChild(strong);
      }
    }
  }

  function setText(el, value) {
    if (el) el.textContent = value || "";
  }

  function init(root) {
    if (root.getAttribute("data-jcv-ready") === "true") return;
    root.setAttribute("data-jcv-ready", "true");

    var S = readStrings(root);
    var title = root.querySelector("[data-jcv-title]");
    var body = root.querySelector("[data-jcv-body]");
    var confirm = root.querySelector("[data-jcv-confirm]");
    var confirmLabel = root.querySelector("[data-jcv-confirm-label]");
    var retry = root.querySelector("[data-jcv-retry]");
    var status = root.querySelector("[data-jcv-status]");
    var link = root.querySelector("[data-jcv-link]");

    /* The one copy of the credential, for this page view only. */
    var token = readToken();
    stripTokenFromUrl();

    var busy = false;

    function focusResult(el) {
      if (!el || typeof el.focus !== "function") return;
      try {
        el.focus();
      } catch (e) {
        /* Focus is a courtesy; the live region already announced. */
      }
    }

    /* A finished outcome: the button is gone, so focus moves to the result or
     * it would be lost to <body> and a keyboard user would be stranded. */
    var wallBox = root.querySelector("[data-jcv-wall]");
    var wallLink = root.querySelector("[data-jcv-wall-link]");
    var wallNote = root.querySelector("[data-jcv-wall-note]");

    /* Shown only after a confirmed backing, and only when this browser can
     * actually reach that Office Wall. Otherwise the recovery note explains
     * how to get back in, rather than offering a link that would fail. */
    /* M28 — the redirect destination, carrying NO credential.
     *
     * `officeWallUrl()` already answers the question that matters — it returns
     * "" unless this browser holds a share token or a participant grant for
     * the office — so reachability is decided by the same code the manual Wall
     * link has always used. The difference is only what travels: this strips
     * the `#access=` fragment, because a redirect this page performs itself
     * must not put a credential in the address bar. The share token is in
     * storage either way, so nothing is lost.
     */
    function wallDestination(slug) {
      var href = officeWallUrl(slug);
      if (!href) return "";
      try {
        var url = new URL(href);
        url.hash = "";
        return url.toString();
      } catch (e) {
        return "";
      }
    }

    function showWallRoute(slug) {
      var href = officeWallUrl(slug);
      if (href && wallBox && wallLink) {
        wallLink.setAttribute("href", href);
        wallBox.hidden = false;
        return;
      }
      if (wallNote) {
        setText(wallNote, S.wallMissing || "");
        wallNote.hidden = !S.wallMissing;
      }
    }

    function settle(titleText, bodyText, company, showLink) {
      setText(title, titleText);
      setMessage(body, bodyText, company || S.companyFallback);
      if (confirm) confirm.hidden = true;
      if (retry) retry.hidden = true;
      if (link && showLink) link.hidden = false;
      setText(status, "");
      focusResult(title);
    }

    /* Recoverable: the backing was not decided, so the action stays available. */
    function offerRetry(titleText, bodyText) {
      setText(title, titleText);
      setMessage(body, bodyText, "");
      if (confirm) confirm.hidden = true;
      if (retry) {
        retry.hidden = false;
        retry.removeAttribute("aria-disabled");
      }
      setText(status, S.statusError || "");
      focusResult(retry);
    }

    function setBusy(value) {
      busy = value;
      [confirm, retry].forEach(function (button) {
        if (!button) return;
        if (value) {
          button.setAttribute("aria-disabled", "true");
        } else {
          button.removeAttribute("aria-disabled");
        }
      });
      setText(confirmLabel, value ? S.submitting || "" : S.confirm || "");
      setText(status, value ? S.statusBusy || "" : "");
    }

    async function verify() {
      /* Double-submit guard. The backing is idempotent server-side, but a
       * second in-flight request would still race the two responses. */
      if (busy) return;
      setBusy(true);

      var response;
      try {
        response = await fetch(VERIFY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({ token: token }),
          credentials: "same-origin"
        });
      } catch (e) {
        /* Network failure. `e` is deliberately not read: a fetch rejection can
         * carry the request, and the request carries the token. */
        setBusy(false);
        offerRetry(S.errorTitle, S.errorBody);
        return;
      }

      var payload = null;
      try {
        payload = await response.json();
      } catch (e) {
        payload = null;
      }

      /* 422 is the backend's single "this link will never work" status. It
       * distinguishes exactly two reasons and nothing else. */
      if (response.status === 422) {
        setBusy(false);
        var reason = payload && typeof payload === "object" ? payload.reason : null;
        if (reason === "expired") {
          settle(S.expiredTitle, S.expiredBody, "", true);
        } else {
          settle(S.invalidTitle, S.invalidBody, "", true);
        }
        return;
      }

      /* Rate limited or briefly unavailable. Retryable, with a "wait a moment"
       * message rather than the generic failure. */
      if (response.status === 429 || response.status === 503) {
        setBusy(false);
        offerRetry(S.rateTitle || S.errorTitle, S.rateBody || S.errorBody);
        return;
      }

      if (!response.ok || !payload || typeof payload !== "object" || payload.ok !== true) {
        /* 400, 404, 405, 5xx, an unparseable body or an unexpected shape. The
         * backing's fate is unknown, so this stays retryable and says nothing
         * about what went wrong. */
        setBusy(false);
        offerRetry(S.errorTitle, S.errorBody);
        return;
      }

      /* F-1/F-2 — this link was already redeemed. The response carries no
       * credential by design, so nothing is stored — not even the "backed"
       * marker. A browser that already holds a share token or grant for the
       * office goes to its Wall; any other browser is told how to get back in.
       * Never retried. */
      if (payload.linkUsed === true) {
        var usedDestination = wallDestination(
          typeof payload.officeSlug === "string" ? payload.officeSlug : ""
        );
        if (usedDestination) {
          window.location.replace(usedDestination);
          return;
        }
        setBusy(false);
        settle(S.usedTitle || S.alreadyTitle, S.usedBody || S.alreadyBody, "", true);
        return;
      }

      /* M28 — the busy state is NOT cleared here any more. On the success path
       * the next thing that happens is a navigation, and re-enabling the
       * button first would flash it live for a frame and invite a second
       * click at exactly the wrong moment. It is cleared on the one success
       * branch that stays on this page, and on every failure branch above. */
      /* M21 — capture the verified contributor credential.
       *
       * Stored on BOTH the first confirmation and a replay: the backend
       * reissues it on an "already verified" response precisely so somebody
       * who confirmed on their phone can open the same link on a laptop and
       * contribute there. A null token means the recovery window has lapsed,
       * and is deliberately NOT treated as authorization. */
      /* Recorded on a replay too: "already verified" is still confirmation
       * that this backing is counted, which is what the status row states. */
      mergeCampaignStore(
        typeof payload.officeSlug === "string" ? payload.officeSlug : "",
        {
          /* M28 — office-scoped READ access, stored in its own right. It is
           * never a stand-in for the contributor credential beside it: a
           * response that omits that one leaves this visitor able to read the
           * Wall and not to write to it, which is the existing model. */
          participantToken: payload.participantToken,
          participantTokenExpiresAt: payload.participantTokenExpiresAt,
          accessToken: payload.accessToken,
          contributorToken: payload.contributorToken,
          backingVerified: true
        }
      );

      var officeSlug = typeof payload.officeSlug === "string" ? payload.officeSlug : "";

      /* M28 — STRAIGHT INTO THE WALL.
       *
       * One confirmation click now lands the coworker on the Office Wall; the
       * success screen is gone from the normal path. This is what makes the
       * flow work on a second device: a coworker who opens the email on their
       * phone has never held a share link, and the participant grant stored a
       * few lines above is the credential that lets the Wall load for them.
       *
       * Taken on a replay too — the backing is confirmed either way — but only
       * when the Wall is genuinely reachable. Without a grant or a share token
       * the old screen is kept, because redirecting would land them on a Wall
       * that cannot load and say nothing about why.
       *
       * `replace()` keeps the spent verification page out of the back history.
       */
      var destination = wallDestination(officeSlug);
      if (destination) {
        window.location.replace(destination);
        return;
      }

      setBusy(false);
      if (payload.alreadyVerified === true) {
        settle(S.alreadyTitle, S.alreadyBody, payload.companyName, true);
      } else {
        settle(S.successTitle, S.successBody, payload.companyName, true);
      }

      /* No credential to carry, so the Wall route is explained rather than
       * taken. */
      showWallRoute(officeSlug);
    }

    if (!token) {
      /* No link, or a reload after the token was stripped. Never a failure
       * message about the backing itself — nothing was attempted. */
      settle(S.noTokenTitle, S.noTokenBody, "", true);
      return;
    }

    if (confirm) {
      confirm.hidden = false;
      confirm.addEventListener("click", verify);
    }
    if (retry) retry.addEventListener("click", verify);
  }

  function boot() {
    var roots = document.querySelectorAll("[data-jcv-root]");
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The Theme Customizer re-renders a section without reloading the page. */
  document.addEventListener("shopify:section:load", boot);
})();
